import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  cost?: number;
}

export function Chat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionCost, setSessionCost] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const apiKey = localStorage.getItem('clz_api_key') || '';

  // Check if user has a running instance — redirect to deploy if no instance at all
  useEffect(() => {
    api.getMe().then(data => {
      if (!data.instance) {
        navigate('/deploy', { replace: true });
      }
    }).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg: Message = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
        body: JSON.stringify({
          model: 'claude-auto',
          max_tokens: 4096,
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error?.message || err.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      const text = data.content?.[0]?.text || data.content || '';
      const cost = data.usage ? ((data.usage.input_tokens || 0) * 0.000003 + (data.usage.output_tokens || 0) * 0.000015) : 0;

      setMessages(prev => [...prev, { role: 'assistant', content: text, model: data.model, cost }]);
      setSessionCost(prev => prev + cost);
    } catch (err: any) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handlePrompt = (text: string) => { setInput(text); inputRef.current?.focus(); };

  const modelLabel = (m?: string) => {
    if (!m) return '';
    if (m.includes('haiku')) return 'haiku';
    if (m.includes('sonnet')) return 'sonnet';
    if (m.includes('opus')) return 'opus';
    return m.split('/').pop() || m;
  };

  return (
    <div className="chat-container">
      <aside className="sidebar">
        <div className="logo">
          <span className="logo-icon">🦞</span>
          <div>
            <span className="logo-text">CLAWZEMPIC</span>
            <div className="logo-sub">The Inference Diet</div>
          </div>
        </div>
        <nav className="nav">
          <Link to="/dashboard" className="nav-item"><span className="nav-icon">◆</span><span>Overview</span></Link>
          <Link to="/chat" className="nav-item active"><span className="nav-icon">💬</span><span>Chat</span></Link>
          <Link to="/settings" className="nav-item"><span className="nav-icon">⚙</span><span>Settings</span></Link>
          <Link to="/deploy" className="nav-item"><span className="nav-icon">+</span><span>New Instance</span></Link>
        </nav>
        <div className="sidebar-footer">
          <a href="#">Privacy</a><span className="footer-sep">&middot;</span><a href="#">Terms</a><span className="footer-sep">&middot;</span><a href="#">Docs</a>
          <br />&copy; 2026 Clawzempic
        </div>
      </aside>

      <main className="chat-main">
        <div className="chat-header">
          <h1 className="chat-header-title">Chat</h1>
          <div className="chat-header-center">
            <div className="model-indicator-dot"></div>
            <span>Auto routing</span>
          </div>
          <div className="session-cost">${sessionCost.toFixed(4)} this session</div>
        </div>

        <div className="chat-messages">
          <div className="chat-messages-inner">
            {messages.length === 0 && (
              <div className="message">
                <div className="message-header">
                  <div className="message-avatar">🦞</div>
                  <div className="message-info"><span className="message-name">Clawzempic</span></div>
                </div>
                <div className="message-bubble">Hey! What are you working on?</div>
                <div className="suggested-prompts">
                  <div className="prompt-card" onClick={() => handlePrompt('What models are available?')}>What models are available?</div>
                  <div className="prompt-card" onClick={() => handlePrompt('Write a hello world in Python')}>Write a hello world in Python</div>
                  <div className="prompt-card" onClick={() => handlePrompt('Explain how model routing works')}>Explain how model routing works</div>
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`message${msg.role === 'user' ? ' user' : ''}`}>
                <div className="message-header">
                  <div className={`message-avatar${msg.role === 'user' ? ' user-avatar' : ''}`}>
                    {msg.role === 'user' ? 'Y' : '🦞'}
                  </div>
                  <div className="message-info">
                    <span className="message-name">{msg.role === 'user' ? 'You' : 'Clawzempic'}</span>
                  </div>
                </div>
                <div className="message-bubble" style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                {msg.model && (
                  <div className="model-tag">{modelLabel(msg.model)} · ${(msg.cost || 0).toFixed(4)}</div>
                )}
              </div>
            ))}

            {loading && (
              <div className="message">
                <div className="message-header">
                  <div className="message-avatar">🦞</div>
                  <div className="message-info"><span className="message-name">Clawzempic</span></div>
                </div>
                <div className="message-bubble" style={{ color: 'var(--text-muted)' }}>Thinking...</div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="chat-input-container">
          <div className="chat-input-wrapper">
            <div className="chat-input-bar">
              <input
                ref={inputRef}
                type="text"
                className="chat-input"
                placeholder="Message Clawzempic..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              <button className="chat-send-button" onClick={sendMessage} disabled={loading || !input.trim()}>▶</button>
            </div>
            <div className="session-summary">
              <span>Session: ${sessionCost.toFixed(4)}</span>
              <span>{messages.filter(m => m.role === 'user').length} messages</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
